/**
 * Pure helpers backing the Services & Secrets panel.
 *
 * Kept framework-free so they can be unit-tested in the app's node test env
 * (mirrors `utilities/activity.ts`). These derive per-service state from a
 * `GET /projects/:id/vault` response joined with the live
 * `GET /projects/:id/services` state (molecule's hosted backend is the
 * DEFAULT — the backend auto-wires brokered mode from the project's installed
 * packages), and resolve which endpoint a toggle action should call. No
 * plaintext secret ever flows through here — only the masked summaries the
 * backend returns.
 *
 * @module
 */

import type { ServiceCatalogEntry } from './services-catalog.js'

// ---------------------------------------------------------------------------
// GET /projects/:id/services — live per-project service state
// ---------------------------------------------------------------------------

/**
 * One service's live state as returned by `GET /projects/:id/services`
 * (mirrors the API's `ServiceState` in App/Resource/Services/service.ts).
 * Each GET also triggers the backend's live package sync + default-wiring
 * reconcile, so polling this endpoint IS the awareness mechanism for
 * mid-session package installs.
 */
export interface LiveServiceState {
  /** Backend service category (matches the catalog 1:1). */
  category: string
  /** Brokerability class. */
  serviceClass: 'A' | 'C'
  /** The project's installed packages use this service. */
  present: boolean
  /** Molecule can broker it TODAY (currently only `ai`). */
  brokerSupported: boolean
  /** ONLY set when `brokerSupported`; BYO detection for others stays client-side from the vault list. */
  mode?: 'brokered' | 'unconfigured'
  /** Active brokered provider (e.g. `anthropic`). */
  provider?: string
  /** Wireable providers (only when `brokerSupported`). */
  providers?: string[]
  /** User explicitly turned the molecule default off. */
  optedOut: boolean
}

/** The billing block on `GET /projects/:id/services` — the effective plan's broker rate card. */
export interface ServicesBilling {
  /** Effective plan category (expiry-aware): `anonymous` | `free` | `pro` | `team`. */
  planCategory: string
  /** Monthly hosted (broker) usage included in the plan, in cents. */
  includedBrokerCents: number
  /** Cost-plus multiplier for metered overage (1.5 = provider cost +50%). */
  overageMarkup: number
  /** Free-plan broker trial: hosted requests per month. */
  freeTrialRequests: number
}

/** Shape of the `GET /projects/:id/services` response. */
export interface ServicesStateResponse {
  /** Per-category live state, present-first ordering. */
  services: LiveServiceState[]
  /** The effective plan's broker rate card. */
  billing: ServicesBilling
  /** The LIVE installed `@molecule/*` list (synced from the sandbox). */
  packages: string[]
}

/** Minimal HTTP surface {@link loadServicesState} needs (matches the app's HttpClient). */
export interface ServicesHttp {
  get<T = unknown>(url: string): Promise<{ data?: T }>
}

/**
 * Loads the live services state from `GET /projects/:id/services`. Centralizes
 * the endpoint string + response validation so the same wiring is
 * unit-testable with a mock client and reused by the panel.
 * `refresh` asks the server to re-scan the sandbox for installed packages NOW
 * rather than serve what its TTL already has. That scan is several remote
 * round trips into the user's own microVM, so it belongs to a user action
 * (opening a panel) — a background poll must never ask for it, which is what
 * made an open panel re-scan the sandbox every ~20 seconds indefinitely.
 *
 * @param http - The HTTP client.
 * @param projectId - The project id.
 * @param opts.refresh - Force a fresh sandbox package sync (user-initiated).
 * @returns The parsed state, or null when the body is missing/malformed.
 */
export async function loadServicesState(
  http: ServicesHttp,
  projectId: string,
  opts: { refresh?: boolean } = {},
): Promise<ServicesStateResponse | null> {
  const path = `/projects/${projectId}/services${opts.refresh ? '?refresh=1' : ''}`
  const res = await http.get<ServicesStateResponse>(path)
  const data = res?.data
  if (!data || !Array.isArray(data.services)) return null
  return {
    services: data.services,
    billing: data.billing,
    packages: Array.isArray(data.packages) ? data.packages : [],
  }
}

/** The catalog split by live presence: services the app uses first, the rest after. */
export interface CatalogGroups {
  /** Catalog entries whose service the project's installed packages use ("In your app"). */
  inApp: ServiceCatalogEntry[]
  /** Everything else ("More services"). */
  more: ServiceCatalogEntry[]
}

/**
 * Splits the catalog against an explicit set of "present" categories: entries
 * whose category is in the set come first ("In your app"), the rest under
 * "More services". Within each group Class A (brokerable) entries sort before
 * Class C (BYO-only); the stable sort otherwise preserves catalog order.
 *
 * The unified Environment view uses this directly so it can widen presence
 * beyond the live `GET /services` flags (a setup integration whose
 * `serviceCategory` maps to a catalog category implies presence even while the
 * live state is unavailable).
 * @param catalog - The static catalog (labels/descriptions/envKeys).
 * @param present - The categories considered "in your app".
 * @returns The grouped catalog entries.
 */
export function splitCatalogByPresence(
  catalog: readonly ServiceCatalogEntry[],
  present: ReadonlySet<string>,
): CatalogGroups {
  const classOrder = (entries: ServiceCatalogEntry[]): ServiceCatalogEntry[] =>
    [...entries].sort((a, b) =>
      a.serviceClass === b.serviceClass ? 0 : a.serviceClass === 'A' ? -1 : 1,
    )
  return {
    inApp: classOrder(catalog.filter((e) => present.has(e.category))),
    more: classOrder(catalog.filter((e) => !present.has(e.category))),
  }
}

/**
 * Groups the catalog by the live per-project presence: entries whose category
 * is `present` in the live state come first ("In your app"), the rest under
 * "More services". Within each group Class A (brokerable) entries sort before
 * Class C (BYO-only); the stable sort otherwise preserves catalog order. With
 * no live data (still loading / endpoint unavailable) everything lands in
 * `more`.
 * @param catalog - The static catalog (labels/descriptions/envKeys).
 * @param services - The live `GET /services` state, or null while unavailable.
 * @returns The grouped catalog entries.
 */
export function groupCatalogByPresence(
  catalog: readonly ServiceCatalogEntry[],
  services: readonly LiveServiceState[] | null | undefined,
): CatalogGroups {
  const present = new Set((services ?? []).filter((s) => s.present).map((s) => s.category))
  return splitCatalogByPresence(catalog, present)
}

/** Source of a vault entry as reported by `GET /vault`. */
export type VaultSource = 'byo' | 'broker' | 'config'

/**
 * Which environment a value applies to.
 *
 * `'all'` is the shared value both environments see; `'dev'` and `'prod'`
 * override it for one environment only. A `'prod'` value is never injected into
 * the dev sandbox — that is the point of the scope.
 */
export type EnvScope = 'all' | 'dev' | 'prod'

/** Every scope, in the order the UI presents them. */
export const ENV_SCOPES: readonly EnvScope[] = ['all', 'dev', 'prod']

/** One masked secret as returned by `GET /projects/:id/vault`. */
export interface VaultSecret {
  /** The secret/env key (e.g. `ANTHROPIC_API_KEY`). */
  key: string
  /** Masked value (e.g. `sk-…abcd`) — never plaintext. */
  masked: string
  /** Where the value comes from. */
  source: VaultSource
  /**
   * Which environment the value applies to. Absent on responses from an API
   * that predates per-environment values — treat as `'all'`.
   */
  environment?: EnvScope
}

/**
 * Collapse a scoped secret list to what ONE environment resolves to: the shared
 * values with that environment's overrides applied on top.
 *
 * The panel's service rows ask "is this key configured?", and the honest answer
 * depends on which environment you mean — a prod-only Stripe key leaves dev
 * unconfigured. `'all'` resolves the shared values alone.
 *
 * @param secrets - Every scope's secrets, as returned by `GET /vault`.
 * @param scope - The environment to resolve for.
 * @returns The secrets that apply, one entry per key.
 */
export function resolveSecretsForScope(
  secrets: readonly VaultSecret[],
  scope: EnvScope,
): VaultSecret[] {
  const byKey = new Map<string, VaultSecret>()
  for (const secret of secrets) {
    if ((secret.environment ?? 'all') === 'all') byKey.set(secret.key, secret)
  }
  if (scope !== 'all') {
    for (const secret of secrets) {
      if (secret.environment === scope) byKey.set(secret.key, secret)
    }
  }
  return [...byKey.values()]
}

/** Shape of the `GET /projects/:id/vault` response. */
export interface VaultResponse {
  secrets: VaultSecret[]
  count: number
}

/** The mode a service is currently configured in. */
export type ServiceMode = 'brokered' | 'byo' | 'unconfigured'

/** Derived state for one catalog service. */
export interface ServiceState {
  /**
   * Whether the category is brokered — from the live `GET /services` mode when
   * the backend brokers the category, else from `source:'broker'` vault keys.
   */
  brokered: boolean
  /** BYO env keys that have a stored value, mapped to their masked value. */
  byoMasked: Record<string, string>
  /** Convenience: whether at least one BYO key is set. */
  byoConfigured: boolean
  /** Overall mode (brokered wins, then byo, else unconfigured). */
  mode: ServiceMode
  /** User explicitly turned the molecule default off (from the live state). */
  optedOut: boolean
}

/** Status-chip descriptor for a service row. */
export interface ServiceStatusChip {
  /** Default (English) label. */
  label:
    | 'Molecule backend — on by default'
    | 'Molecule backend — active'
    | 'BYO set'
    | 'Default off'
    | 'Not configured'
  /** Semantic tone used to pick chip colors in the component. */
  tone: 'broker' | 'byo' | 'none'
  /** i18n key for the label. */
  key:
    | 'services.chip.brokeredDefault'
    | 'services.chip.brokeredActive'
    | 'services.chip.byo'
    | 'services.chip.defaultOff'
    | 'services.chip.none'
}

/**
 * Indexes a vault response by key for O(1) lookups.
 * @param vault - The vault response (or null while loading).
 * @returns A map of key → secret.
 */
function indexByKey(vault: VaultResponse | null): Map<string, VaultSecret> {
  const map = new Map<string, VaultSecret>()
  for (const secret of vault?.secrets ?? []) {
    map.set(secret.key, secret)
  }
  return map
}

/**
 * Returns true if the category has any brokered secret in the vault.
 *
 * The backend tags brokered entries with `source:'broker'`. We match on the
 * category being present among brokered keys: brokered secrets are keyed by
 * the category's provider env keys, so any brokered entry whose key belongs to
 * one of the category's providers means that category is brokered.
 * @param entry - The catalog entry to test.
 * @param vault - The vault response.
 * @returns Whether the category is currently brokered.
 */
export function isCategoryBrokered(
  entry: ServiceCatalogEntry,
  vault: VaultResponse | null,
): boolean {
  const byKey = indexByKey(vault)
  for (const provider of entry.providers) {
    for (const key of provider.envKeys) {
      if (byKey.get(key)?.source === 'broker') return true
    }
  }
  return false
}

/**
 * Collects the masked values for a category's BYO-sourced keys.
 * @param entry - The catalog entry.
 * @param vault - The vault response.
 * @returns A map of env key → masked value for every BYO key that is set.
 */
export function byoMaskedForCategory(
  entry: ServiceCatalogEntry,
  vault: VaultResponse | null,
): Record<string, string> {
  const byKey = indexByKey(vault)
  const out: Record<string, string> = {}
  for (const provider of entry.providers) {
    for (const key of provider.envKeys) {
      const secret = byKey.get(key)
      if (secret && secret.source === 'byo') {
        out[key] = secret.masked
      }
    }
  }
  return out
}

/**
 * Derives the full per-service state from a vault response joined with the
 * live `GET /services` state. When the backend brokers the category
 * (`live.brokerSupported`) its `mode` is authoritative for brokered-ness;
 * otherwise brokered detection falls back to `source:'broker'` vault keys.
 * Brokered takes precedence over BYO when both happen to be present.
 * @param entry - The catalog entry.
 * @param vault - The vault response (null while loading → unconfigured).
 * @param live - The category's live backend state, when available.
 * @returns The derived service state.
 */
export function deriveServiceState(
  entry: ServiceCatalogEntry,
  vault: VaultResponse | null,
  live?: LiveServiceState | null,
): ServiceState {
  const brokered = live?.brokerSupported
    ? live.mode === 'brokered'
    : isCategoryBrokered(entry, vault)
  const byoMasked = byoMaskedForCategory(entry, vault)
  const byoConfigured = Object.keys(byoMasked).length > 0
  const mode: ServiceMode = brokered ? 'brokered' : byoConfigured ? 'byo' : 'unconfigured'
  return { brokered, byoMasked, byoConfigured, mode, optedOut: live?.optedOut === true }
}

/**
 * Maps a service mode (+ the explicit opt-out flag) to its status-chip
 * descriptor. Molecule's hosted backend is the default, so brokered without an
 * opt-out reads "on by default"; brokered WITH an opt-out (possible
 * transiently) reads "active"; an opt-out with nothing configured reads
 * "Default off".
 * @param mode - The derived service mode.
 * @param optedOut - Whether the user explicitly turned the molecule default off.
 * @returns The chip label + tone + i18n key.
 */
export function statusChip(mode: ServiceMode, optedOut = false): ServiceStatusChip {
  switch (mode) {
    case 'brokered':
      return optedOut
        ? {
            label: 'Molecule backend — active',
            tone: 'broker',
            key: 'services.chip.brokeredActive',
          }
        : {
            label: 'Molecule backend — on by default',
            tone: 'broker',
            key: 'services.chip.brokeredDefault',
          }
    case 'byo':
      return { label: 'BYO set', tone: 'byo', key: 'services.chip.byo' }
    default:
      return optedOut
        ? { label: 'Default off', tone: 'none', key: 'services.chip.defaultOff' }
        : { label: 'Not configured', tone: 'none', key: 'services.chip.none' }
  }
}

// ---------------------------------------------------------------------------
// Billing frame — how the broker's cost is framed for the user's plan
// ---------------------------------------------------------------------------

/** How the hosted-backend cost is framed under the broker toggle. */
export type BillingFrame =
  /** Free/anonymous: the monthly broker trial. */
  | { kind: 'freeTrial'; requests: number }
  /** Paid plans: included monthly usage + the overage markup. */
  | { kind: 'included'; includedCents: number; overageMarkup: number }

/**
 * Resolves the billing frame for the plan's broker rate card: free/anonymous
 * plans get the free-trial framing, pro/team the included-usage framing. An
 * unknown plan category falls back on whether any usage is included.
 * @param billing - The `GET /services` billing block, or null while loading.
 * @returns The frame, or null when billing data is unavailable.
 */
export function billingFrame(billing: ServicesBilling | null | undefined): BillingFrame | null {
  if (!billing) return null
  const paid =
    billing.planCategory === 'pro' || billing.planCategory === 'team'
      ? true
      : billing.planCategory === 'free' || billing.planCategory === 'anonymous'
        ? false
        : billing.includedBrokerCents > 0
  return paid
    ? {
        kind: 'included',
        includedCents: billing.includedBrokerCents,
        overageMarkup: billing.overageMarkup,
      }
    : { kind: 'freeTrial', requests: billing.freeTrialRequests }
}

/** A resolved API request the panel should issue for a toggle/save/remove. */
export interface VaultAction {
  method: 'POST' | 'DELETE' | 'PUT'
  /** Path relative to the API base, with `:id` already substituted. */
  path: string
  /** Request body, if any. */
  body?: Record<string, unknown>
}

/**
 * Resolves the request for enabling brokered mode on a category.
 *
 * The server picks the provider from the project's OWN packages, so no provider
 * is sent: naming one here is how an openai-bonded project ended up wired to
 * anthropic.
 *
 * @param projectId - The project id.
 * @param category - The backend secret category.
 * @param opts - Enable options.
 * @param opts.replaceOwnKeys - The user confirmed that turning the hosted
 *   backend on replaces a key they saved themselves. Without it the server
 *   refuses (409 `vault.error.brokerWouldReplaceOwnKey`) rather than
 *   overwriting the only copy of their credential.
 * @returns `POST /projects/:id/vault/broker` with `{ category }`.
 */
export function enableBrokerAction(
  projectId: string,
  category: string,
  opts: { replaceOwnKeys?: boolean } = {},
): VaultAction {
  return {
    method: 'POST',
    path: `/projects/${projectId}/vault/broker`,
    body: opts.replaceOwnKeys ? { category, replaceOwnKeys: true } : { category },
  }
}

/**
 * Resolves the request for disabling brokered mode on a category.
 * @param projectId - The project id.
 * @param category - The backend secret category.
 * @returns `DELETE /projects/:id/vault/broker` with `{ category }`.
 */
export function disableBrokerAction(projectId: string, category: string): VaultAction {
  return {
    method: 'DELETE',
    path: `/projects/${projectId}/vault/broker`,
    body: { category },
  }
}

/**
 * Resolves the request for storing a BYO secret.
 * @param projectId - The project id.
 * @param key - The env key to store.
 * @param value - The plaintext secret to encrypt + store.
 * @param scope - Which environment the value applies to (default: shared).
 * @returns `PUT /projects/:id/vault/:key` with `{ value, environment }`.
 */
export function putSecretAction(
  projectId: string,
  key: string,
  value: string,
  scope: EnvScope = 'all',
): VaultAction {
  return {
    method: 'PUT',
    path: `/projects/${projectId}/vault/${encodeURIComponent(key)}`,
    body: { value, environment: scope },
  }
}

/**
 * Resolves the request for removing a stored secret.
 *
 * Without a `scope` the key is removed from EVERY environment — "remove this
 * key" means the key, not one of its values. Passing a scope removes only that
 * environment's override, leaving the shared value (and the other environment)
 * in place.
 *
 * @param projectId - The project id.
 * @param key - The env key to remove.
 * @param scope - Optional single environment to clear.
 * @returns `DELETE /projects/:id/vault/:key`.
 */
export function deleteSecretAction(projectId: string, key: string, scope?: EnvScope): VaultAction {
  return {
    method: 'DELETE',
    path:
      `/projects/${projectId}/vault/${encodeURIComponent(key)}` +
      (scope ? `?environment=${scope}` : ''),
  }
}

/** Minimal HTTP surface a {@link runVaultAction} needs (matches the app's HttpClient). */
export interface VaultHttp {
  post(url: string, data?: unknown): Promise<unknown>
  put(url: string, data?: unknown): Promise<unknown>
  delete(url: string, config?: { data?: unknown }): Promise<unknown>
}

/**
 * Dispatches a resolved {@link VaultAction} through an HTTP client. Centralizes
 * the method→client-call mapping so the same wiring is unit-testable with a
 * mock client and reused by the panel (no duplicated endpoint strings).
 *
 * DELETE carries its body via the request config's `data` field (the app's
 * HttpClient.delete takes a config, not a body argument).
 * @param http - The HTTP client.
 * @param action - The resolved action.
 * @returns The client call's promise.
 */
export function runVaultAction(http: VaultHttp, action: VaultAction): Promise<unknown> {
  switch (action.method) {
    case 'POST':
      return http.post(action.path, action.body)
    case 'PUT':
      return http.put(action.path, action.body)
    case 'DELETE':
      return http.delete(action.path, action.body ? { data: action.body } : undefined)
  }
}
